import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FormLabel } from "./FormLabel";
import { goToNext, goPrevious, useFormValue, useFieldAutoFocus } from '../common';
import { useFluidForm } from "../FormContext";
import { buildFluidFormClient } from "../../../fluidForm.client";
import "../common-form-styles.css";
import { useFormInstanceSelect } from "../FormInstance.service";
import { NylasBookingForm, NylasScheduling } from "@nylas/react";
import { Icon, Modal } from "semantic-ui-react";
const InputRegion = styled.input.withConfig({
    shouldForwardProp: (prop) => !['isFocused', 'width'].includes(prop),
}).attrs(props => ({
    className: `fluid-input ${props.isFocused ? 'fluid-input-focus' : ''}`
})) `
  width: ${props => props.width ? `${props.width}px` : "100%"};
  
`;
const SlotsContainer = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;
const SlotButton = styled.button `
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${props => props.$selected ? '#2563eb' : '#e5e7eb'};
  background: ${props => props.$selected ? '#dbeafe' : '#ffffff'};
  color: #111827;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: #2563eb; }
`;
const ErrorText = styled.div `
  color: #dc2626;
  font-size: 12px;
  margin-top: 6px;
`;
const CTAScheduleButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
    box-shadow: 0 4px 8px rgba(249, 115, 22, 0.3);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  i.icon {
    font-size: 16px;
    margin: 0 !important;
  }
`;
export function FieldSchedulingComponent({ fieldPath, options, ...props }) {
    const fluidForm = useFluidForm();
    const config = fluidForm.getFieldConfigByFieldPath(fieldPath);
    const omitLabel = options?.omitLabel || false;
    const [value, setValue] = useFormValue(fieldPath);
    const [reservedEventId, setReservedEventId] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [configuration, setConfiguration] = useState();
    const fluidClient = useMemo(() => buildFluidFormClient(), []);
    useEffect(() => {
        if (value?.eventId) {
            setReservedEventId(value.eventId);
        }
    }, [value?.slotData?.start, value?.slotData?.end, value?.eventId]);
    useEffect(() => {
        setLoading(true);
        fluidClient.getSchedulingConfigurations().then((configs) => {
            if (configs.length > 0) {
                // We are always picking the first configuration at the moment.
                setConfiguration(configs[0]);
            }
            else {
                setError('No meeting configurations found');
            }
            setLoading(false);
        }).catch((error) => {
            setError(error.message || 'Failed to load configurations');
            setLoading(false);
        });
    }, []);
    const onSuccessfullReservation = useCallback((v) => {
        setValue(v);
        setReservedEventId(v.eventId);
    }, [setValue, setReservedEventId]);
    return React.createElement(React.Fragment, null,
        !omitLabel && React.createElement(FormLabel, { config: config, options: options, fieldPath: fieldPath }),
        reservedEventId && (React.createElement("div", { className: "text-xs text-green-600 mt-2" },
            "Reserved! Confirmation: ",
            reservedEventId)),
        loading && React.createElement("div", { className: "text-xs text-gray-500 mt-2" }, "Loading\u2026"),
        error && React.createElement(ErrorText, null, error),
        (!reservedEventId && !loading && !error) && ((config.schedulingConfig.variant === 'calendar-widget' ?
            React.createElement(FieldSchedulingCalendarWidgetComponent, { fieldPath: fieldPath, options: options, onSuccessfullReservation: onSuccessfullReservation, configuration: configuration }) :
            React.createElement(FieldSchedulingNaturalLanguageComponent, { fieldPath: fieldPath, options: options, onSuccessfullReservation: onSuccessfullReservation }))));
}
function FieldSchedulingCalendarWidgetComponent({ fieldPath, options, onSuccessfullReservation, configuration }) {
    const [openModal, setOpenModal] = useState(false);
    const fluidForm = useFluidForm();
    const ref = useRef(null);
    useFieldAutoFocus(ref, fieldPath, options?.autoFocus);
    const email = fluidForm.getPlainValue('email');
    const name = fluidForm.getPlainValue('fullName');
    const handleCloseModal = () => {
        setOpenModal(false);
    };
    return React.createElement(React.Fragment, null,
        React.createElement(CTAScheduleButton, { type: "button", onClick: () => setOpenModal(true), disabled: !configuration, title: "Book a call" },
            React.createElement(Icon, { name: "calendar" }),
            React.createElement("span", null, "Book A Call")),
        React.createElement(Modal, { open: openModal, onClose: handleCloseModal, size: "small", closeIcon: true, closeOnDimmerClick: false },
            React.createElement(Modal.Content, { style: { padding: 0 } },
                React.createElement(NylasScheduling, { ref: ref, configurationId: configuration.id, isLoading: !configuration, bookingInfo: {
                        primaryParticipant: { email: email, name: name },
                    }, onBookedEventInfo: (event) => {
                        const eventData = event.detail.data;
                        const slotData = {
                            start: new Date(eventData.start_time * 1000).toISOString(),
                            end: new Date(eventData.end_time * 1000).toISOString(),
                        };
                        const eventId = eventData.event_id;
                        const participants = {
                            primaryParticipant: eventData.guest,
                            ...(eventData.additional_guests ? { guests: eventData.additional_guests } : {}),
                        };
                        onSuccessfullReservation({ slotData, eventId, participants });
                    }, defaultLanguage: "en" }))));
}
function FieldSchedulingNaturalLanguageComponent({ fieldPath, options, onSuccessfullReservation }) {
    const fluidForm = useFluidForm();
    const [query, setQuery] = useState('Today/Tomorrow Working hours');
    const [readyQuery, setReadyQuery] = useState();
    const ref = useRef(null);
    useFieldAutoFocus(ref, fieldPath, options?.autoFocus);
    const handleKeyDown = (event) => {
        const fieldName = event.target.getAttribute("data-fieldName");
        if (event.key === "Tab") {
            event.preventDefault();
            goToNext(fieldName, fluidForm);
            return;
        }
        if (event.key === "Backspace" && event.target.value === "") {
            event.preventDefault();
            goPrevious(fieldName, fluidForm);
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            updateReadyQuery(query);
            return;
        }
    };
    const updateReadyQuery = useCallback((query) => {
        const trimmed = query?.trim() || '';
        if (trimmed.length > 0) {
            setReadyQuery(trimmed);
        }
    }, [setReadyQuery]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { position: 'relative' } },
            React.createElement(InputRegion, { ref: ref, name: fieldPath, id: `ff-${fieldPath}`, type: "text", "data-fieldname": fieldPath, isFocused: fluidForm.isFocused(fieldPath), suppressContentEditableWarning: true, onFocus: () => fluidForm.setCurrentField(fieldPath), onBlur: () => fluidForm.unfocusField(fieldPath), onKeyDown: handleKeyDown, onChange: (e) => setQuery(e.target.value), value: query }),
            React.createElement("button", { type: "button", onClick: () => { updateReadyQuery(query); }, className: "absolute right-7 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs px-2 py-0.5 border border-gray-200 rounded bg-white", title: "Search availability" }, "Check"),
            query?.length > 0 && (React.createElement("button", { type: "button", onClick: () => { setQuery(''); }, className: "absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm", title: "Clear" }, "\u00D7"))),
        React.createElement(SchedulingModal, { fieldPath: fieldPath, query: readyQuery, onSuccessfullReservation: onSuccessfullReservation })));
}
function SchedulingModal({ fieldPath, query, onSuccessfullReservation }) {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [reserving, setReserving] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const fluidForm = useFluidForm();
    const fluidClient = useMemo(() => buildFluidFormClient(), []);
    const submissionId = useFormInstanceSelect((state) => state.submissionId);
    const fetchAvailabilityForQuery = async (q) => {
        try {
            setIsOpen(true);
            setLoading(true);
            setError(null);
            // Ask backend agent for availability based on the user's request
            const resp = await fluidClient.getAvailability(fluidForm.getFormKey(), q, submissionId);
            const mapped = resp.slots ? resp.slots.map((s) => ({
                start: typeof s.start === 'string' ? s.start : new Date(s.start).toISOString(),
                end: typeof s.end === 'string' ? s.end : new Date(s.end).toISOString(),
            })) : [];
            setSlots(mapped);
            const message = (resp && (resp.message || resp.text || resp.info)) || null;
            setInfo(typeof message === 'string' ? message : message ? JSON.stringify(message) : null);
            if (mapped.length === 0) {
                setError('No slots available');
            }
        }
        catch (e) {
            const errMsg = e;
            // @TODO: FLU-193 Handle this.
            const msg = typeof errMsg === 'string' ? errMsg : errMsg?.message ? String(errMsg.message) : 'Failed to load availability';
            setError(msg);
            setSlots([]);
            setInfo(null);
        }
        finally {
            setLoading(false);
        }
    };
    const handleReserve = useCallback(async (p) => {
        try {
            if (!selectedSlot) {
                setError('Please select a time slot to proceed with the booking');
                return;
            }
            setReserving(true);
            setError(null);
            const email = fluidForm.getPlainValue('email');
            const name = fluidForm.getPlainValue('fullName');
            const participants = { primaryParticipant: p.primaryParticipant,
                ...(p?.guests ? { additional_guests: p.guests } : {})
            };
            const result = await fluidClient.reserveSlot({ ...selectedSlot,
                participants
            });
            if (!result.success) {
                const errAny = result?.message;
                const msg = typeof errAny === 'string' ? errAny : errAny ? JSON.stringify(errAny) : 'This slot is no longer available';
                setError(msg);
                return;
            }
            setIsOpen(false);
            onSuccessfullReservation({ slotData: selectedSlot, eventId: result.eventId, participants });
        }
        catch (e) {
            const errAny = e;
            // @TODO: FLU-193 Handle this.
            const msg = typeof errAny === 'string' ? errAny : errAny?.message ? String(errAny.message) : 'Failed to reserve slot';
            setError(msg);
        }
        finally {
            setReserving(false);
        }
    }, [query, selectedSlot]);
    useEffect(() => {
        if (query && query.trim().length > 0) {
            console.log('fetching availability for query', query);
            fetchAvailabilityForQuery(query);
        }
    }, [query]);
    return React.createElement(Modal, { open: isOpen, onClose: () => { setIsOpen(false); }, size: "large", closeIcon: true, closeOnDimmerClick: false },
        React.createElement(Modal.Content, { style: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' } },
            loading && (React.createElement("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' } },
                React.createElement("div", { className: "text-xs text-gray-500" }, "Loading available slots\u2026"))),
            !loading && slots && slots.length > 0 && (React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '16px' } },
                React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '12px' }, "data-fieldname": fieldPath },
                    !loading && info && (React.createElement("div", { className: "text-xs text-gray-600 text-left" }, info)),
                    React.createElement(SlotsContainer, { "data-fieldname": fieldPath }, slots.slice(0, 12).map((s, idx) => {
                        const startDate = new Date(s.start);
                        const label = startDate.toLocaleString(undefined, {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        });
                        return (React.createElement(SlotButton, { key: `${s.start}-${idx}`, onClick: () => setSelectedSlot(s), disabled: reserving, "$selected": (selectedSlot?.start === s.start && selectedSlot?.end === s.end), title: `${new Date(s.start).toLocaleString()} - ${new Date(s.end).toLocaleString()}` }, label));
                    }))),
                React.createElement(NylasBookingForm, { themeConfig: {
                        "--nylas-primary": "#f97316",
                        "--nylas-base-100": "#ffffff",
                        "--nylas-base-200": "#f8fafc",
                        "--nylas-border-radius": "10px"
                    }, onDetailsConfirmed: (event) => {
                        handleReserve(event.detail);
                    }, onBackButtonClicked: () => { setIsOpen(false); } }))),
            error && (React.createElement("div", { style: { display: 'flex', justifyContent: 'center', width: '100%' } },
                React.createElement(ErrorText, null, error)))));
}
